import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiFileText, FiUploadCloud, FiTrash2, FiCheckCircle, FiAlertCircle,
  FiLoader, FiTarget, FiMapPin, FiEye, FiPlay, FiExternalLink, FiEdit3
} from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { confirmToast } from '../../lib/confirmToast';

// ==============================================================================
// UploadModal (factures gaz)
// ==============================================================================
const UploadModal = ({
  isOpen,
  onClose,
  modalContext,
  isUploadingFacture,
  existingDocs,
  onUpload,
  onView,
  onDelete,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  if (!isOpen || !modalContext) return null;

  const { pce, year } = modalContext;

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploadingFacture) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isUploadingFacture) return;

    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)
    );

    if (dropped.length === 0) return;
    onUpload(dropped);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg p-3 max-w-md w-full border border-gray-200 shadow-lg">

        {/* En-tête */}
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xs font-semibold text-gray-800">Upload Factures Gaz</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-sm leading-none px-1"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Contexte PCE / Année */}
        <div className="mb-2 p-2 bg-blue-50 rounded-md border border-blue-200">
          <p className="text-[10px] font-semibold text-blue-800 leading-tight">
            {pce?.site_name || `PCE ${pce?.pce_number}`}
          </p>
          <p className="text-[9px] text-blue-700">Année fiscale : {year}</p>
        </div>

        {/* Zone drag & drop + clic */}
        <label
          htmlFor="file-upload-ticgn"
          className={`w-full border-2 border-dashed rounded-md p-4 flex flex-col justify-center items-center text-center transition-all cursor-pointer select-none ${
            isUploadingFacture
              ? 'border-gray-200 bg-gray-50 cursor-default'
              : isDragging
                ? 'border-blue-500 bg-blue-50 scale-[1.01]'
                : 'border-blue-300 bg-white hover:border-blue-400 hover:bg-blue-50/30'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isUploadingFacture ? (
            <>
              <FiLoader className="mx-auto h-6 w-6 text-blue-500 animate-spin" />
              <span className="mt-1.5 block text-[10px] font-semibold text-gray-600">
                Envoi en cours...
              </span>
            </>
          ) : isDragging ? (
            <>
              <FiUploadCloud className="mx-auto h-7 w-7 text-blue-500" />
              <span className="mt-1.5 block text-[11px] font-bold text-blue-600">
                Déposez les fichiers ici
              </span>
            </>
          ) : (
            <>
              <FiUploadCloud className="mx-auto h-6 w-6 text-gray-400" />
              <span className="mt-1.5 block text-[10px] font-semibold text-gray-700">
                Glisser-déposer ou{' '}
                <span className="text-blue-500 underline">cliquer pour sélectionner</span>
              </span>
              <p className="text-[9px] text-gray-400 mt-0.5">PDF, PNG, JPG • 10 MB max</p>
            </>
          )}
        </label>

        <input
          id="file-upload-ticgn"
          type="file"
          className="hidden"
          multiple
          accept="application/pdf,image/jpeg,image/png"
          onChange={(e) => onUpload(Array.from(e.target.files))}
          disabled={isUploadingFacture}
        />

        {/* Liste des documents existants */}
        {existingDocs.length > 0 && (
          <div className="mt-3">
            <h4 className="text-[10px] font-semibold text-gray-700 mb-1">
              Documents existants ({existingDocs.length})
            </h4>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
              {existingDocs.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center justify-between text-[10px] px-2 py-1.5 bg-white rounded-md border border-gray-100"
                >
                  <span
                    className="truncate flex items-center gap-1.5 font-mono text-[9px] text-gray-700"
                    title={file.original_file_name}
                  >
                    <FiFileText className="text-gray-500 w-3.5 h-3.5 flex-shrink-0" />
                    {file.original_file_name}
                  </span>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <button
                      onClick={() => onView(file.id)}
                      className="w-6 h-6 inline-flex items-center justify-center rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                      title="Voir"
                    >
                      <FiEye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(file.id)}
                      className="w-6 h-6 inline-flex items-center justify-center rounded bg-red-50 text-red-600 hover:bg-red-100"
                      title="Supprimer"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {existingDocs.length === 0 && (
          <p className="mt-2 text-center text-[9px] text-gray-400">
            Aucune facture gaz importée pour ce PCE / cette année.
          </p>
        )}
      </div>
    </div>
  );
};

// ==============================================================================
// COMPOSANT PRINCIPAL
// ==============================================================================
const TaxesDetailTicgn = ({ clientData, session }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [pces, setPces] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [analysisResults, setAnalysisResults] = useState({});
  const [isUploadingFacture, setIsUploadingFacture] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState(null);
  const [analyzingStates, setAnalyzingStates] = useState({});

  const fiscalYearOptions = [2023, 2024, 2025, 2026, 2027];
  const [manualModeFlags, setManualModeFlags] = useState({});
  const [premierBilanConfig, setPremierBilanConfig] = useState({});
  const [exonerationFlags, setExonerationFlags] = useState({}); // key: accountId-year → 'J' ou 'K'
  const [pourcentageExonereFlags, setPourcentageExonereFlags] = useState({}); // key: accountId-year → number

  // ==============================================================================
  // CHARGEMENT DES PCE
  // ==============================================================================
  const loadPces = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('pce')
        .select('*')
        .eq('account_id', accountId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPces(data || []);
    } catch (error) {
      console.error('Erreur chargement PCE:', error);
      toast.error('Impossible de charger les points de comptage.');
      setPces([]);
    }
  }, [clientData?.accountId]);

  // ==============================================================================
  // CHARGEMENT DES DOCUMENTS (factures gaz)
  // ==============================================================================
  const loadDocuments = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId || !session?.access_token) return;

    try {
      const { data } = await axios.get(
        `/api/cspe/documents/uploads-s3?accountId=${accountId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (data.success) {
        const filteredDocs = data.documents
          .filter(doc =>
            doc.document_type_id === 'factures_gaz' ||
            doc.document_type_id === 'facture_gaz'
          )
          .map(doc => ({
            ...doc,
            year: doc.year != null ? Number(doc.year) : null,
            pce_id: doc.pce_id ?? null,
          }));

        setDocuments(filteredDocs);
      }
    } catch (error) {
      console.error('Erreur chargement documents gaz:', error.response?.data || error);
      if (error.response?.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error('Impossible de charger les documents.');
      }
    }
  }, [clientData?.accountId, session?.access_token]);

  // ==============================================================================
  // CHARGEMENT DES RÉSULTATS D'ANALYSE
  // ==============================================================================
  const loadAnalysisResults = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId || !session?.access_token) return;

    try {
      // TODO: Adapter l'endpoint pour TICGN quand le backend sera prêt
      // Pour l'instant, on ne charge rien
      setAnalysisResults({});
    } catch (error) {
      console.error('Erreur chargement analyses TICGN:', error);
    }
  }, [clientData?.accountId, session?.access_token]);

  // ==============================================================================
  // CHARGEMENT DES FLAGS MODE MANUEL
  // ==============================================================================
  const loadManualModeFlags = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('ticgn_manual_mode')
        .select('pce_id, year, enabled')
        .eq('account_id', accountId);

      if (error) throw error;

      const flagsMap = (data || []).reduce((acc, row) => {
        if (row.enabled) acc[`${row.pce_id}-${row.year}`] = true;
        return acc;
      }, {});

      setManualModeFlags(flagsMap);
    } catch (error) {
      console.error('Erreur chargement mode manuel TICGN:', error);
    }
  }, [clientData?.accountId]);

  const toggleManualMode = useCallback(async (pceId, year, enabled) => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    const key = `${pceId}-${year}`;
    setManualModeFlags(prev => ({ ...prev, [key]: enabled }));

    try {
      const { error } = await supabase
        .from('ticgn_manual_mode')
        .upsert(
          { account_id: accountId, pce_id: pceId, year, enabled },
          { onConflict: 'account_id,pce_id,year' }
        );

      if (error) throw error;
      toast.success(enabled ? 'Mode manuel activé' : 'Mode manuel désactivé');
    } catch (error) {
      console.error('Erreur toggle mode manuel TICGN:', error);
      toast.error('Erreur lors du changement de mode');
      setManualModeFlags(prev => ({ ...prev, [key]: !enabled }));
    }
  }, [clientData?.accountId]);

  // ==============================================================================
  // CHARGEMENT / SAUVEGARDE OPTION EXONERATION (G11=J, G12=K)
  // ==============================================================================
  const loadExonerationFlags = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('ticgn_dossier_years')
        .select('fiscal_year, exoneration_option, pourcentage_exonere')
        .eq('account_id', accountId);

      if (error) throw error;

      const flagsMap = {};
      const pctMap = {};
      (data || []).forEach(row => {
        const key = `${accountId}-${row.fiscal_year}`;
        flagsMap[key] = row.exoneration_option || 'J';
        pctMap[key] = row.pourcentage_exonere ?? 100;
      });

      setExonerationFlags(flagsMap);
      setPourcentageExonereFlags(pctMap);
    } catch (error) {
      console.error('Erreur chargement exoneration TICGN:', error);
    }
  }, [clientData?.accountId]);

  const saveExonerationOption = useCallback(async (year, option) => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    const key = `${accountId}-${year}`;
    setExonerationFlags(prev => ({ ...prev, [key]: option }));

    try {
      const { error } = await supabase
        .from('ticgn_dossier_years')
        .upsert(
          { account_id: accountId, fiscal_year: year, exoneration_option: option, updated_at: new Date().toISOString() },
          { onConflict: 'account_id,fiscal_year' }
        );

      if (error) throw error;
      toast.success(`Exonération ${option === 'J' ? 'G11' : 'G12'} enregistrée pour ${year}`);
    } catch (error) {
      console.error('Erreur sauvegarde exoneration TICGN:', error);
      toast.error('Erreur lors de la sauvegarde');
      setExonerationFlags(prev => ({ ...prev, [key]: prev[key] === 'J' ? 'K' : 'J' }));
    }
  }, [clientData?.accountId]);

  const savePourcentageExonere = useCallback(async (year, pct) => {
    const accountId = clientData?.accountId;
    if (!accountId) return;
    const key = `${accountId}-${year}`;
    const val = Math.min(100, Math.max(0, parseFloat(pct) || 100));
    setPourcentageExonereFlags(prev => ({ ...prev, [key]: val }));
    try {
      const { error } = await supabase
        .from('ticgn_dossier_years')
        .upsert(
          { account_id: accountId, fiscal_year: year, pourcentage_exonere: val, updated_at: new Date().toISOString() },
          { onConflict: 'account_id,fiscal_year' }
        );
      if (error) throw error;
    } catch (error) {
      console.error('Erreur sauvegarde pourcentage exonere:', error);
    }
  }, [clientData?.accountId]);

  // ==============================================================================
  // CHARGEMENT INITIAL
  // ==============================================================================
  useEffect(() => {
    const loadInitialData = async () => {
      if (!clientData?.accountId) return;
      setIsLoading(true);
      await Promise.all([loadPces(), loadDocuments(), loadAnalysisResults(), loadManualModeFlags(), loadExonerationFlags()]);
      setIsLoading(false);
    };
    loadInitialData();
  }, [clientData?.accountId, loadPces, loadDocuments, loadAnalysisResults, loadManualModeFlags, loadExonerationFlags]);

  // ==============================================================================
  // UPLOAD FICHIER
  // ==============================================================================
  const uploadFileToS3 = async (file, type, context) => {
    const { pce, year } = context;
    const toastId = toast.loading('Préparation de l\'envoi...');
    const accountId = clientData?.accountId;

    if (!accountId) {
      toast.error('ID du compte manquant.', { id: toastId });
      throw new Error('accountId manquant');
    }

    // URL presignée
    const { data: presignData } = await axios.post(
      '/api/cspe/documents/request-upload-url',
      { fileName: file.name, accountId },
      { headers: { Authorization: `Bearer ${session.access_token}` } }
    );
    if (!presignData.success) throw new Error('URL d\'upload non obtenue');

    const { uploadUrl, s3Key } = presignData.data;
    toast.loading('Envoi en cours...', { id: toastId });

    // Upload S3
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });

    toast.loading('Finalisation...', { id: toastId });

    // Confirmation backend
    await axios.post(
      '/api/cspe/documents/confirm-upload',
      {
        s3Key,
        originalFileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        type: 'factures_gaz',
        year,
        pce_id: pce?.id || null,
        accountId,
      },
      { headers: { Authorization: `Bearer ${session.access_token}` } }
    );

    toast.success(`${file.name} envoyé !`, { id: toastId });
  };

  const handleFactureUpload = async (files, context) => {
    if (!files || files.length === 0) return;
    setIsUploadingFacture(true);

    try {
      for (const file of files) {
        await uploadFileToS3(file, 'factures_gaz', context);
      }
      await loadDocuments();
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error(error.response?.data?.message || 'Upload échoué');
    } finally {
      setIsUploadingFacture(false);
    }
  };

  // ==============================================================================
  // ACTIONS DOCUMENTS
  // ==============================================================================
  const handleViewDocument = async (docId) => {
    const toastId = toast.loading('Ouverture...');
    try {
      const { data } = await axios.get(
        `/api/cspe/documents/uploads-s3/${docId}/download`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (data.success && data.url) {
        window.open(data.url, '_blank');
        toast.dismiss(toastId);
      } else {
        throw new Error('URL non reçue');
      }
    } catch (error) {
      toast.error('Impossible d\'ouvrir le document', { id: toastId });
    }
  };

  const handleDeleteDocument = async (docId) => {
    const doc = documents.find(d => d.id === docId);

    const confirmed = await confirmToast({
      title: 'Supprimer ce document ?',
      subtitle: doc?.original_file_name
        ? `"${doc.original_file_name}" sera définitivement supprimé.`
        : 'Cette action est irréversible.',
      confirmLabel: 'Supprimer',
      variant: 'danger',
    });
    if (!confirmed) return;

    const toastId = toast.loading('Suppression...');
    try {
      await axios.delete(`/api/cspe/documents/uploads-s3/${docId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      toast.success('Document supprimé', { id: toastId });
      await loadDocuments();
    } catch (error) {
      toast.error('Suppression échouée', { id: toastId });
    }
  };

  // ==============================================================================
  // HELPERS
  // ==============================================================================
  const getPceDocuments = (pceId, year) =>
    documents.filter(doc => doc.pce_id === pceId && doc.year === year);

  const openModal = (pce, year) => {
    setModalContext({ type: 'facture', pce, year });
    setIsModalOpen(true);
  };

  const navigateToTicgnAnalysis = (pceId, year, result) => {
    const pce = pces.find(p => p.id === pceId);
    navigate(`/dashboard/admin/analyse-ticgn/${clientData.accountId}`, {
      state: { pce, year, analysisResult: result },
    });
  };

  // ==============================================================================
  // ÉTATS DE CHARGEMENT / ERREUR
  // ==============================================================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <FiLoader className="animate-spin h-5 w-5 text-blue-500 mr-2" />
        <span className="text-[10px] text-gray-600">Chargement…</span>
      </div>
    );
  }

  if (!clientData?.accountId) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
        <FiAlertCircle className="mx-auto h-7 w-7 text-red-500 mb-1.5" />
        <h3 className="text-xs font-semibold text-red-900">Erreur</h3>
        <p className="text-[10px] text-red-700 mt-1">Données client non chargées</p>
      </div>
    );
  }

  if (pces.length === 0) {
    return (
      <div className="space-y-2">
        <div className="bg-gradient-to-r from-blue-500 to-teal-600 rounded-lg p-3 text-white">
          <div className="flex items-center gap-2">
            <FiTarget className="h-5 w-5" />
            <div>
              <h1 className="text-sm font-bold leading-tight">Analyse TICGN</h1>
              <p className="text-[10px] text-blue-100 leading-tight">
                Optimisation des taxes gaz pour {clientData.name}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
          <FiAlertCircle className="mx-auto h-7 w-7 text-yellow-500 mb-1.5" />
          <h3 className="text-xs font-semibold text-yellow-900">Aucun point de comptage</h3>
          <p className="text-[10px] text-yellow-700 mt-1">Ce compte ne possède aucun PCE.</p>
          <p className="text-[9px] text-yellow-700/80 mt-1">Ajoutez des PCE dans l'onglet dédié.</p>
        </div>
      </div>
    );
  }

  // ==============================================================================
  // RENDU PRINCIPAL
  // ==============================================================================
  return (
    <div className="space-y-2">

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-teal-600 rounded-lg px-3 py-2 text-white">
        <div className="flex items-center gap-2">
          <FiTarget className="h-5 w-5" />
          <div className="min-w-0">
            <h1 className="text-sm font-bold leading-tight">Analyse TICGN</h1>
            <p className="text-[10px] text-blue-100 leading-tight truncate">
              Optimisation des taxes gaz pour {clientData.name}
            </p>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200">
          <h2 className="text-xs font-semibold text-gray-900 flex items-center">
            <FiTarget className="mr-1.5 text-blue-500 w-3.5 h-3.5" />
            Factures par PCE
          </h2>
          <p className="text-[10px] text-gray-600 mt-0.5">Cliquez sur une cellule pour gérer les factures gaz.</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[220px]">
                  Point de Comptage (PCE)
                </th>
                {fiscalYearOptions.map(year => (
                  <th
                    key={year}
                    className="px-2 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase tracking-wider min-w-[92px]"
                  >
                    {year}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-100">
              {pces.map(pce => (
                <tr key={pce.id} className="hover:bg-gray-50/50">

                  {/* Colonne PCE */}
                  <td className="px-2 py-2 whitespace-nowrap sticky left-0 bg-white z-10 border-r border-gray-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                        <FiTarget className="h-3 w-3 text-blue-600" />
                      </div>
                      <div className="ml-2 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-900 leading-tight truncate">
                          {pce.site_name}
                        </div>
                        <div className="text-[9px] text-gray-500 font-mono leading-tight truncate">
                          {pce.pce_number}
                        </div>
                        <div className="text-[9px] text-gray-400 flex items-center gap-1 leading-tight">
                          <FiMapPin className="w-3 h-3" />
                          <span className="truncate">
                            {pce.address?.city || '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Colonnes années */}
                  {fiscalYearOptions.map(year => {
                    const analysisKey = `${pce.id}-${year}`;
                    const result = analysisResults[analysisKey];
                    const isAnalyzing = analyzingStates[analysisKey];
                    const docsForCell = getPceDocuments(pce.id, year);
                    const docsCount = docsForCell.length;
                    const hasDocs = docsCount > 0;
                    const isManualMode = !!manualModeFlags[analysisKey];

                    return (
                      <td key={year} className="px-2 py-2 whitespace-nowrap text-center align-top">
                        <div className="flex flex-col items-center gap-1.5">

                          {/* Bouton gestion factures */}
                          <button
                            onClick={() => openModal(pce, year)}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold transition-colors ${
                              hasDocs
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : isManualMode
                                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {hasDocs ? (
                              <>
                                <FiCheckCircle className="mr-1 h-3.5 w-3.5" />
                                {docsCount} facture{docsCount > 1 ? 's' : ''}
                              </>
                            ) : isManualMode ? (
                              <>
                                <FiEdit3 className="mr-1 h-3.5 w-3.5" />
                                Mode manuel
                              </>
                            ) : (
                              <>
                                <FiAlertCircle className="mr-1 h-3.5 w-3.5" />
                                À fournir
                              </>
                            )}
                          </button>

                          {/* Résultat d'analyse */}
                          {result ? (
                            <>
                              <div className="text-center">
                                <div className="text-[12px] font-bold text-green-600 leading-tight">
                                  {Math.abs(result.montant_ticgn_a_rembourser || 0).toLocaleString(
                                    'fr-FR', { style: 'currency', currency: 'EUR' }
                                  )}
                                </div>
                                <div className="text-[9px] text-green-700 font-semibold leading-tight">
                                  économies
                                </div>
                              </div>

                              <button
                                onClick={() => navigateToTicgnAnalysis(pce.id, year, result)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                              >
                                <FiExternalLink className="w-3 h-3" />
                                Détails
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-[9px] text-gray-400 italic">
                                {isAnalyzing ? (
                                  <span className="flex items-center gap-1 text-blue-600">
                                    <FiLoader className="animate-spin w-3 h-3" />
                                    Analyse…
                                  </span>
                                ) : (
                                  'Non analysé'
                                )}
                              </span>

                              {/* Bouton saisie manuelle */}
                              {isManualMode && (
                                <button
                                  onClick={() => navigateToTicgnAnalysis(pce.id, year, null)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-md transition-colors"
                                >
                                  <FiEdit3 className="w-3 h-3" />
                                  Saisie manuelle
                                </button>
                              )}
                            </>
                          )}

                          {/* Toggles */}
                          <div className="mt-1 w-full space-y-1">
                            {/* Toggle mode manuel */}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={isManualMode}
                              onClick={() => toggleManualMode(pce.id, year, !isManualMode)}
                              className="flex items-center gap-1.5 justify-center mx-auto"
                            >
                              <span
                                className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                  isManualMode ? 'bg-purple-600' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    isManualMode ? 'translate-x-3' : 'translate-x-0'
                                  }`}
                                />
                              </span>
                              <span className={`text-[9px] font-medium ${isManualMode ? 'text-purple-800' : 'text-gray-500'}`}>
                                Manuel
                              </span>
                            </button>

                            {/* Toggle 1er bilan */}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={!!premierBilanConfig[`${pce.id}-${year}`]?.enabled}
                              onClick={() => {
                                const key = `${pce.id}-${year}`;
                                const next = !premierBilanConfig[key]?.enabled;
                                setPremierBilanConfig((prev) => ({
                                  ...prev,
                                  [key]: {
                                    ...prev[key],
                                    enabled: next,
                                    startDate: next ? prev[key]?.startDate || '' : '',
                                  },
                                }));
                              }}
                              className="flex items-center gap-1.5 justify-center mx-auto"
                            >
                              <span
                                className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                  premierBilanConfig[`${pce.id}-${year}`]?.enabled ? 'bg-amber-500' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    premierBilanConfig[`${pce.id}-${year}`]?.enabled ? 'translate-x-3' : 'translate-x-0'
                                  }`}
                                />
                              </span>
                              <span className={`text-[9px] font-medium ${premierBilanConfig[`${pce.id}-${year}`]?.enabled ? 'text-amber-800' : 'text-gray-500'}`}>
                                1er bilan
                              </span>
                            </button>

                            {premierBilanConfig[`${pce.id}-${year}`]?.enabled && (
                              <input
                                type="date"
                                value={premierBilanConfig[`${pce.id}-${year}`]?.startDate || ''}
                                onChange={(e) =>
                                  setPremierBilanConfig((prev) => ({
                                    ...prev,
                                    [`${pce.id}-${year}`]: {
                                      ...prev[`${pce.id}-${year}`],
                                      startDate: e.target.value,
                                    },
                                  }))
                                }
                                className="mt-1 w-full text-[9px] border border-amber-300 rounded px-1 py-0.5 bg-white"
                              />
                            )}

                            {/* Selecteur exoneration G11/G12 */}
                            <div className="flex items-center gap-1 justify-center mt-1">
                              <select
                                value={exonerationFlags[`${clientData?.accountId}-${year}`] || 'J'}
                                onChange={(e) => saveExonerationOption(year, e.target.value)}
                                className="text-[8px] border border-teal-300 rounded px-1 py-0.5 bg-white text-teal-800 font-medium cursor-pointer"
                                title={exonerationFlags[`${clientData?.accountId}-${year}`] === 'K'
                                  ? 'G12 — Déshydratation légumes/plantes aromatiques'
                                  : 'G11 — Installation intensive exposée à la concurrence internationale'}
                              >
                                <option value="J">Exo. G11 — Intensive</option>
                                <option value="K">Exo. G12 — Déshydratation</option>
                                <option value="O">Exo. G17 — Agricole</option>
                              </select>
                            </div>
                            {/* Pourcentage exoneré */}
                            <div className="flex items-center gap-1 justify-center mt-0.5">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={pourcentageExonereFlags[`${clientData?.accountId}-${year}`] ?? 100}
                                onChange={(e) => savePourcentageExonere(year, e.target.value)}
                                className="w-10 text-[8px] border border-teal-300 rounded px-1 py-0.5 bg-white text-teal-800 font-medium text-center"
                                title="Pourcentage exonéré (ex: 70 = 70% tarif réduit)"
                              />
                              <span className="text-[8px] text-teal-700">% exo.</span>
                            </div>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalContext={modalContext}
        isUploadingFacture={isUploadingFacture}
        existingDocs={
          modalContext
            ? getPceDocuments(modalContext.pce?.id, modalContext.year)
            : []
        }
        onUpload={(files) => handleFactureUpload(files, modalContext)}
        onView={handleViewDocument}
        onDelete={handleDeleteDocument}
      />

    </div>
  );
};

export default TaxesDetailTicgn;
